import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import { REVIEWS, FAQ_DATA } from '@/mocks/comics';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.koreancomic.cn/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.koreancomic.cn/about' },
      ],
    },
    {
      '@type': 'Organization',
      name: '韩国漫画网',
      url: 'https://www.koreancomic.cn',
      description: '韩国漫画网是专业的韩漫在线阅读平台，提供免费、无删减的韩国漫画资源，涵盖恋爱、奇幻、动作、悬疑等多种题材。',
      foundingDate: '2023',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contact@koreancomic.cn',
        availableLanguage: ['Chinese'],
      },
    },
    {
      '@type': 'WebPage',
      name: '关于我们 - 韩国漫画网',
      url: 'https://www.koreancomic.cn/about',
      about: { '@id': 'https://www.koreancomic.cn/#organization' },
    },
  ],
};

export default function AboutPage() {
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSuccess(true);
      return;
    }
    setFormSuccess(true);
    form.reset();
    setTimeout(() => setFormSuccess(false), 3000);
  };

  return (
    <>
      <SEO
        title="关于我们 - 韩国漫画网"
        description="韩国漫画网是专业的韩漫在线阅读平台，提供免费、无删减的韩国漫画资源。了解我们的品牌故事、用户口碑及联系方式。"
        keywords="韩国漫画网,关于我们,韩漫平台,免费韩漫,联系我们"
        schema={aboutSchema}
      />

      <main className="pt-20">
        <section className="py-10 md:py-14 px-4 md:px-6 max-w-4xl mx-auto">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700">关于我们</span>
          </nav>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6">关于我们</h1>

          <div className="prose-sm text-foreground-600 leading-relaxed space-y-5">
            <h2 className="text-xl font-semibold text-foreground-950">品牌故事</h2>
            <p>
              韩国漫画网成立于2023年，是专注于韩漫在线阅读的优质中文平台。我们深知韩漫爱好者对于高质量阅读体验的追求，因此从创立之初就以"提供最纯粹、最完整的韩漫阅读体验"为核心理念。
            </p>
            <p>
              在韩流文化席卷全球的今天，韩国漫画以其独特的叙事风格、精美的画风和丰富多样的题材受到了越来越多中文读者的喜爱。然而，许多读者在寻找韩漫资源时常常面临翻译质量参差不齐、内容被删减、更新不及时等问题。韩国漫画网的诞生，正是为了解决这些痛点。
            </p>
            <p>
              我们的团队由一群热爱韩漫的资深读者和专业内容运营人员组成。每一部上线作品都经过团队的精心挑选和审校，确保翻译准确流畅、画质清晰、内容完整。我们与多家韩国漫画平台和翻译团队建立了长期合作关系，致力于第一时间为中文读者带来最新的韩漫连载内容。
            </p>

            <h2 className="text-xl font-semibold text-foreground-950 mt-8">我们的优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              {[
                { icon: 'ri-shield-check-line', title: '无删减', desc: '保留原作全部内容，原汁原味的韩漫阅读体验' },
                { icon: 'ri-flashlight-line', title: '更新快', desc: '韩网更新后第一时间同步中文版本' },
                { icon: 'ri-translate', title: '高质量翻译', desc: '专业翻译团队精心打磨，读起来流畅自然' },
                { icon: 'ri-smartphone-line', title: '全设备适配', desc: '手机、平板、电脑都能获得最佳阅读体验' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3 p-4 bg-background-50 rounded-lg border border-background-200">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className={item.icon} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                    <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="text-xl font-semibold text-foreground-950 mt-8">用户口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              {REVIEWS.map((review, idx) => (
                <div key={idx} className="p-4 bg-background-50 rounded-lg border border-background-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-foreground-900">{review.name}</span>
                    <div className="flex items-center">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-yellow-500 text-xs" />
                      ))}
                    </div>
                    <span className="text-xs text-foreground-400 ml-auto">{review.date}</span>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>

            <h2 className="text-xl font-semibold text-foreground-950 mt-8">联系我们</h2>
            <p>
              如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。我们会在1-2个工作日内回复您的消息。
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <div className="flex-1 p-4 bg-background-50 rounded-lg border border-background-200">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-mail-line text-primary-500" />
                  <span className="text-sm font-medium text-foreground-900">电子邮箱</span>
                </div>
                <p className="text-sm text-foreground-600">contact@koreancomic.cn</p>
              </div>
              <div className="flex-1 p-4 bg-background-50 rounded-lg border border-background-200">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-time-line text-primary-500" />
                  <span className="text-sm font-medium text-foreground-900">工作时间</span>
                </div>
                <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="about-contact-form"
              className="mt-6 p-5 bg-background-50 rounded-lg border border-background-200 space-y-4"
            >
              <h3 className="text-base font-semibold text-foreground-950">发送消息</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1">姓名</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm text-foreground-700 mb-1">主题</label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入消息主题"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1">消息内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                  placeholder="请输入您的消息（最多500字）"
                />
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              {formError && (
                <p className="text-sm text-red-500">{formError}</p>
              )}
              {formSuccess && (
                <p className="text-sm text-green-600">消息已发送成功！我们会尽快回复您。</p>
              )}

              <button
                type="submit"
                className="bg-primary-500 text-background-50 px-6 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                发送消息
              </button>
            </form>
          </div>
        </section>
      </main>
    </>
  );
}